import type { Tag, SortField, FilterEntity } from '@/types/tag';
import { HttpClient } from '@/api/services/http';
import { mockTags } from '@/api/tagsApi';

const API_URL = '/tag';

export interface TagsResponse {
  data: any;
  tags: Tag[];
  total: number;
}

export interface TagParams {
  searchQuery?: string;
  filterEntity?: FilterEntity;
  sortField?: SortField;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export const tagService = {
  async fetchTags(params: TagParams): Promise<TagsResponse> {
    try {
      const response = await HttpClient.apiClient.get<TagsResponse>(API_URL, {
        params,
      });
      return response.data;
    } catch (error) {
      console.log('API call failed, using mock data for tags');
      // Return mock data when API fails
      const { searchQuery = '', filterEntity = 'all', sortField = 'name', sortDirection = 'asc', page = 1, limit = 1000 } = params;

      let filteredTags = mockTags.filter(tag =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
      );

      if (filterEntity !== 'all') {
        filteredTags = filteredTags.filter(tag => tag.counts[filterEntity] > 0);
      }

      filteredTags.sort((a, b) => {
        let valueA: any = sortField === 'name' ? a.name :
          sortField === 'lastUsed' ? new Date(a.lastUsed).getTime() :
            sortField === 'createdAt' ? new Date(a.createdAt).getTime() :
              a.counts[sortField];
        let valueB: any = sortField === 'name' ? b.name :
          sortField === 'lastUsed' ? new Date(b.lastUsed).getTime() :
            sortField === 'createdAt' ? new Date(b.createdAt).getTime() :
              b.counts[sortField];

        if (sortDirection === 'desc') {
          [valueA, valueB] = [valueB, valueA];
        }

        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      });

      const start = (page - 1) * limit;
      const end = start + limit;
      const paginatedTags = filteredTags.slice(start, end);

      return {
        data: paginatedTags,
        tags: paginatedTags,
        total: filteredTags.length
      };
    }
  },

  // ✅ Create a new tag
  async createTag(tag: Partial<Tag>): Promise<Tag> {
    try {
      const response = await HttpClient.apiClient.post<Tag>(API_URL, tag);
      console.log('TagService: Created tag:', response.data);
      return response.data.data[0];
    } catch (error) {
      console.error('Error creating tag:', error);
      throw new Error('Failed to create tag');
    }
  },

  // ✅ Update an existing tag
  async updateTag(id: string, tag: Partial<Tag>): Promise<Tag> {
    try {
      const response = await HttpClient.apiClient.put<Tag>(`${API_URL}/${id}`, tag);
      return response.data;
    } catch (error) {
      console.error('Error updating tag:', error);
      throw new Error('Failed to update tag');
    }
  },

  // ✅ Delete tags by ID array
  async deleteTags(ids: string[]): Promise<void> {
    try {
      await HttpClient.apiClient.delete(API_URL, {
        data: { ids },
      });
    } catch (error) {
      console.error('Error deleting tags:', error);
      throw new Error('Failed to delete tags');
    }
  }
};
